import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiUser, FiList } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';
import ContractDrillDownPanel from './ContractDrillDownPanel';

const COLORS = ['#6366F1','#3B82F6','#10B981','#F59E0B','#EF4444','#A855F7','#06B6D4','#F97316','#EC4899','#14B8A6'];

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};
const fmtW = (n) => `${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MT`;
const fmt  = (n) => Number(n || 0).toLocaleString('en-US');

const BAR_METRICS = [
  { key: 'total_value',    label: 'Total $',  fmt: fmtM },
  { key: 'contract_count', label: 'Count',    fmt: v => v },
  { key: 'contract_value', label: 'Ctr $',    fmt: fmtM },
  { key: 'addendum_value', label: 'Adm $',    fmt: fmtM },
  { key: 'contract_weight',label: 'Weight',   fmt: fmtW },
];


const SortableTh = ({ label, sortKey, currentSort, onSort }) => {
  const active = currentSort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-gray-600 tw-cursor-pointer hover:tw-bg-gray-100 tw-select-none tw-whitespace-nowrap"
    >
      {label}{active && <span className="tw-ml-1 tw-text-gray-400">{currentSort.dir === 'desc' ? '▼' : '▲'}</span>}
    </th>
  );
};

SortableTh.propTypes = {
  label: PropTypes.node.isRequired,
  sortKey: PropTypes.string.isRequired,
  currentSort: PropTypes.shape({
    key: PropTypes.string,
    dir: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
  onSort: PropTypes.func.isRequired,
};

const ContractSalesInchargeChart = ({ data, loading, error, baseParams }) => {
  const [metric,     setMetric]     = useState('total_value');
  const [view,       setView]       = useState('list');
  const [drillOpen,  setDrillOpen]  = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillExtra, setDrillExtra] = useState({});
  const [tableSort,  setTableSort]  = useState({ key: 'total_value', dir: 'desc' });

  if (loading) return (
    <Card padding="none" className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card padding="none" className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading sales incharge data</p>
    </Card>
  );

  const items      = [...(data || [])].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const maxVal     = Math.max(...items.map(r => r[metric] || 0), 1);
  const activeFmt  = (BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0]).fmt;

  const tableRows = [...(data || [])].sort((a, b) => {
    const diff = (b[tableSort.key] || 0) - (a[tableSort.key] || 0);
    return tableSort.dir === 'desc' ? diff : -diff;
  });

  const totalContracts = tableRows.reduce((s, r) => s + (r.contract_count  || 0), 0);
  const totalAddendums = tableRows.reduce((s, r) => s + (r.addendum_count  || 0), 0);
  const totalCtrVal    = tableRows.reduce((s, r) => s + (r.contract_value  || 0), 0);
  const totalAdmVal    = tableRows.reduce((s, r) => s + (r.addendum_value  || 0), 0);
  const totalVal       = tableRows.reduce((s, r) => s + (r.total_value     || 0), 0);
  const totalWeight    = tableRows.reduce((s, r) => s + (r.contract_weight || 0), 0);

  const handleSort = (key) => {
    setTableSort(prev =>
      prev.key === key
        ? { ...prev, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    );
  };

  const handleClick = (row) => {
    setDrillTitle(`Sales Incharge: ${row.sales_incharge}`);
    setDrillExtra({ sales_incharges: row.sales_incharge });
    setDrillOpen(true);
  };

  // Per-row rich styles reproduce the colored round rank badges.
  const rankRich = items.reduce((acc, _row, i) => ({
    ...acc,
    [`rank${i}`]: {
      backgroundColor: COLORS[i % COLORS.length],
      color: '#ffffff',
      borderRadius: 10,
      width: 20,
      height: 20,
      align: 'center',
      fontSize: 10,
      fontWeight: 'bold'
    }
  }), {});

  const barOption = {
    textStyle: { fontFamily: CHART_FONT.fontFamily },
    grid: { left: 8, right: 100, top: 4, bottom: 4, containLabel: true },
    tooltip: {
      ...CHART_TOOLTIP,
      formatter: (p) => {
        const row = items[p.dataIndex];
        if (!row) return '';
        return [
          `<b>${escapeHtml(row.sales_incharge || 'Unknown')}</b>`,
          `${(BAR_METRICS.find(m => m.key === metric) || BAR_METRICS[0]).label}: <b>${escapeHtml(String(activeFmt(row[metric] || 0)))}</b>`,
          `<span style="color:#2563EB;font-weight:600">${row.contract_count}C · ${row.addendum_count}A</span>`,
          escapeHtml(fmtM(row.total_value))
        ].join('<br/>');
      }
    },
    xAxis: { type: 'value', show: false, max: maxVal },
    yAxis: {
      type: 'category',
      inverse: true,
      data: items.map(r => r.sales_incharge || 'Unknown'),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...CHART_FONT,
        formatter: (value, index) => `{rank${index}|${index + 1}} {name|${value}}`,
        rich: {
          ...rankRich,
          name: { color: '#374151', fontWeight: 600, fontSize: 11, align: 'left', width: 96, overflow: 'truncate', padding: [0, 0, 0, 4] }
        }
      }
    },
    series: [{
      type: 'bar',
      barWidth: 20,
      cursor: 'pointer',
      showBackground: true,
      backgroundStyle: { color: '#F3F4F6', borderRadius: 10 },
      itemStyle: { borderRadius: 10, opacity: 0.8 },
      label: {
        show: true,
        position: 'right',
        fontSize: 11,
        fontWeight: 'bold',
        color: 'inherit',
        formatter: (p) => String(activeFmt(items[p.dataIndex]?.[metric] || 0))
      },
      data: items.map((r, i) => ({
        value: r[metric] || 0,
        itemStyle: { color: COLORS[i % COLORS.length] }
      }))
    }]
  };

  return (
    <>
      {/* padding="none" — the card manages its own flush header/body/footer,
          the default p-4 body wrapper broke dividers and the sticky table */}
      <Card padding="none" className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2">
            <FiUser size={16} className="tw-text-blue-500" />
            <h3 className="tw-font-bold tw-text-gray-900 tw-mb-0">By Sales Incharge</h3>
            <span className="tw-text-xs tw-text-gray-400">{items.length} people</span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            {/* View toggle */}
            <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
              <button
                onClick={() => setView('list')}
                className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${view === 'list' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
              >
                Chart
              </button>
              <button
                onClick={() => setView('table')}
                className={`tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 tw-border-l tw-border-l-gray-200 ${view === 'table' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
              >
                <FiList size={11} /> Table
              </button>
            </div>

            {/* Metric toggle — bar view only */}
            {view === 'list' && (
              <div className="tw-inline-flex tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden">
                {BAR_METRICS.map((opt, idx) => (
                  <button
                    key={opt.key}
                    onClick={() => setMetric(opt.key)}
                    className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-all tw-cursor-pointer tw-border-0 ${idx > 0 ? 'tw-border-l tw-border-l-gray-200' : ''} ${metric === opt.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-800'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Bar / chart view ── */}
        {view === 'list' && (
          <>
            <div className="tw-p-4 tw-max-h-80 tw-overflow-y-auto">
              {items.length === 0 ? (
                <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
              ) : (
                <ReactECharts
                  option={barOption}
                  style={{ height: Math.max(items.length * 32 + 8, 120) }}
                  notMerge
                  onEvents={{
                    click: (params) => {
                      const row = items[params.dataIndex];
                      if (row) handleClick(row);
                    }
                  }}
                />
              )}
            </div>
            <div className="tw-px-5 tw-py-2 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-400 tw-text-center">
              Click any row to view detailed contracts
            </div>
          </>
        )}

        {/* ── Detail table view ── */}
        {view === 'table' && (
          <div className="tw-overflow-auto tw-max-h-[460px]">
            <table className="tw-w-full tw-text-xs tw-border-collapse">
              <thead>
                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10">
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-500 tw-w-8">#</th>
                  <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600 tw-min-w-28">Sales Incharge</th>
                  <SortableTh label="Contracts"   sortKey="contract_count"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Addendums"   sortKey="addendum_count"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Contract $"  sortKey="contract_value"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Addendum $"  sortKey="addendum_value"  currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Total $"     sortKey="total_value"     currentSort={tableSort} onSort={handleSort} />
                  <SortableTh label="Weight (MT)" sortKey="contract_weight" currentSort={tableSort} onSort={handleSort} />
                  <th className="tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-400"></th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr
                    key={row.sales_incharge}
                    onClick={() => handleClick(row)}
                    className={`tw-border-b tw-border-gray-100 tw-cursor-pointer ${i % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50/40'} hover:tw-bg-blue-50/60 tw-transition-colors`}
                  >
                    <td className="tw-px-3 tw-py-2">
                      <span className="tw-w-5 tw-h-5 tw-rounded-xl tw-inline-flex tw-items-center tw-justify-center tw-text-white tw-text-xs tw-font-bold" style={{ background: COLORS[i % COLORS.length] }}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-800 tw-whitespace-nowrap">{row.sales_incharge || 'Unknown'}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-blue-700">{fmt(row.contract_count)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-text-amber-600">{fmt(row.addendum_count || 0)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmtM(row.contract_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right" style={{ color: (row.addendum_value || 0) < 0 ? '#DC2626' : '#D97706' }}>
                      {(row.addendum_value || 0) !== 0 ? fmtM(row.addendum_value) : '—'}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-emerald-700">{fmtM(row.total_value)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(row.contract_weight)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-blue-400 tw-cursor-pointer">↗</td>
                  </tr>
                ))}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr className="tw-border-t-2 tw-border-gray-300 tw-bg-gray-100 tw-font-bold">
                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-text-xs" colSpan={2}>Total ({tableRows.length} people)</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmt(totalContracts)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600">{fmt(totalAddendums)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-blue-700">{fmtM(totalCtrVal)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right" style={{ color: totalAdmVal < 0 ? '#DC2626' : '#D97706' }}>{fmtM(totalAdmVal)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-700">{fmtM(totalVal)}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{fmtW(totalWeight)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <ContractDrillDownPanel
        open={drillOpen}
        title={drillTitle}
        baseParams={baseParams || {}}
        extraParams={drillExtra}
        onClose={() => setDrillOpen(false)}
      />
    </>
  );
};

ContractSalesInchargeChart.propTypes = {
  data:       PropTypes.array,
  loading:    PropTypes.bool,
  error:      PropTypes.any,
  baseParams: PropTypes.object
};
ContractSalesInchargeChart.defaultProps = { data: [], loading: false, error: null, baseParams: {} };

export default ContractSalesInchargeChart;
